import { Avatar, Box, Flex } from '@chakra-ui/react'
import styled from '@emotion/styled'
import { motion } from 'framer-motion'

import { AiOperator } from '@/components/icons'
import { IncidentActionType, IncidentStatus } from '@/graphql/generated/schemas'
import { formatDateTime } from '@/utils/formatDateTime'

import { NonActionItem } from '../types/types'

const StyledFeedItem = styled(motion.div)`
  padding-bottom: 10px;
  width: 100%;
  /* max-width: 460px; */
  &:last-of-type,
  &:first-of-type {
    padding-bottom: 0;
    .item-tail {
      display: none;
    }
    .item-arm {
      display: none;
    }
  }
`

export const itemStatusBgColor = (
  actionType: IncidentActionType | IncidentStatus | null
) => {
  switch (actionType) {
    case null:
    case IncidentStatus.Active:
    case IncidentActionType.ReleaseByOperator:
      return '#fae8eb'
    case IncidentStatus.Resolved:
    case IncidentActionType.Resolved:
      return '#e8fae9'
    case IncidentStatus.InProgress:
    case IncidentActionType.AcknowledgementByOperator:
      return '#FFF0E5'

    default:
      return 'transparent'
  }
}

export const itemStatusIconBgGradientColor = (
  actionType: IncidentActionType | IncidentStatus | NonActionItem | null
) => {
  switch (actionType) {
    case null:
    case IncidentActionType.ReleaseByOperator:
    case IncidentActionType.SetRealToFalse:
    case IncidentActionType.Flag:
      return 'linear(to-t, #D01030, #D01030)'
    case IncidentActionType.Cleared:
    case IncidentActionType.Resolved:
      return 'linear(to-t, #13B0B0, #46EF9F)'
    case NonActionItem.SystemAction:
      return 'linear(to-t, #ffffff, #ffffff)'
    default:
      return 'linear(to-t, brand.100, brand.200)'
  }
}

interface FeedItemIProps {
  actionType?: IncidentActionType | IncidentStatus | null
  heading: string | React.ReactNode
  children?: React.ReactNode
  index?: number
  avatar?: string
  avatarName?: string
  createdAt?: string
  icon?: React.ReactNode
}

// This is a temporary solution to make it work for the demo purposes in Sales env
// Should be reworked in the future to handle custom user avatars
const ConditionalAvatar = ({
  name,
  avatar,
}: {
  name: string
  avatar: string
}) => {
  if (name === 'AI Operator') {
    return (
      <Avatar
        backgroundColor='white'
        icon={<AiOperator />}
        mr='8px'
        mt='4px'
        size='2xs'
      />
    )
  }

  return <Avatar mr='8px' mt='5px' name={name} size='2xs' src={avatar} />
}

export const FeedItem = ({
  actionType,
  heading,
  avatar,
  avatarName,
  createdAt,
  index,
  icon,
  children,
}: FeedItemIProps) => {
  return (
    <StyledFeedItem
      animate={{ opacity: 1, translateX: 0, translateY: 0 }}
      data-testId={'IncidentsPage_FeedItem_' + index}
      initial={{
        opacity: 0,
        translateX: 0,
        translateY: -50,
      }}
      transition={{ duration: 0.3, delay: index ? index * 0.1 : 0 }}
    >
      <Box
        bg={itemStatusBgColor(actionType)}
        borderRadius='lg'
        p='10px'
        pb='12px'
        position='relative'
      >
        <Box
          alignItems='center'
          bgGradient={itemStatusIconBgGradientColor(actionType)}
          borderRadius='base'
          d='flex'
          h='30px'
          justifyContent='center'
          position='absolute'
          w='30px'
          zIndex='2'
        >
          {icon}
        </Box>
        <Box
          borderLeft='2px dotted #E3E3E3'
          className='item-tail'
          h='100%'
          left='24px'
          position='absolute'
          top='40px'
        />
        <Box
          borderTop='2px dotted #E3E3E3'
          className='item-arm'
          left='40px'
          position='absolute'
          top='24px'
          w='17px'
        />
        <Box ml='50px' position='relative' top='2px' w='calc(100% - 50px)'>
          <Flex>
            {avatarName && (
              <ConditionalAvatar avatar={avatar} name={avatarName} />
            )}
            <Box
              flexGrow={1}
              fontSize='16px'
              fontWeight='medium'
              letterSpacing='-0.53px'
            >
              {heading}
            </Box>
          </Flex>
          <Box
            color='#2D2E41'
            fontSize='12px'
            fontWeight='medium'
            letterSpacing='-0.33px'
            opacity='0.8'
          >
            {formatDateTime(createdAt)}
          </Box>
          <Box fontSize='16px' fontWeight='medium' letterSpacing='-0.53px'>
            {children}
          </Box>
        </Box>
      </Box>
    </StyledFeedItem>
  )
}
